import { useState } from "react";
import { Layout } from "@/components/layout";
import {
  useGetMe,
  useListEvents,
  useListEventInventory,
  useListPricingVenues,
  useListPricingSections,
  useListPricingGroups,
  useCreatePricingGroup,
  useUpdatePricingGroup,
  useDeletePricingGroup,
  useBulkPushPricing,
  getListPricingGroupsQueryKey,
  getListEventInventoryQueryKey,
  type PricingGroup,
  type PricingGroupMember,
  type PricingGroupMemberInput,
  type PricingSection,
  type BulkPricePushResult,
  type InventoryListing,
  type Event,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Loader2, Plus, Trash2, Pencil, Send, SlidersHorizontal, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function Pricing() {
  const { data: me } = useGetMe();
  const canPrice = me?.role === "admin" || me?.role === "pricing";

  if (me && !canPrice) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <h2 className="text-lg font-semibold">Access Restricted</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Pricing is available to pricers and admins only.
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div
        data-testid="pricing-scroll"
        className="px-2 sm:px-4 pb-2 sm:pb-4 max-w-6xl mx-auto w-full h-full overflow-y-auto"
      >
        <Tabs defaultValue="groups" className="w-full">
          <div className="sticky top-0 z-20 bg-background pt-2 sm:pt-4 pb-3 border-b border-border">
            <div className="mb-3">
              <h1 className="text-xl font-semibold tracking-tight">Pricing</h1>
              <p className="text-sm text-muted-foreground">
                Organize inventory into venue groups and push bulk recommended prices to pullers.
              </p>
            </div>
            <TabsList>
              <TabsTrigger value="groups">Venue Groups</TabsTrigger>
              <TabsTrigger value="bulk">Bulk Update</TabsTrigger>
            </TabsList>
          </div>
          <TabsContent value="groups" className="mt-4">
            <GroupsTab />
          </TabsContent>
          <TabsContent value="bulk" className="mt-4">
            <BulkUpdateTab />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

function GroupsTab() {
  const { data: venues } = useListPricingVenues();
  const [venue, setVenue] = useState<string>("");

  const effectiveVenue = venue || venues?.[0]?.venue || "";

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-xs">
        <span className="text-muted-foreground">Venue</span>
        <Select value={effectiveVenue} onValueChange={setVenue}>
          <SelectTrigger className="h-8 w-72 text-xs">
            <SelectValue placeholder="Select a venue" />
          </SelectTrigger>
          <SelectContent>
            {(venues ?? []).map((v) => (
              <SelectItem key={v.venue} value={v.venue}>
                {v.venue}
                {v.venueCity ? ` — ${v.venueCity}` : ""} ({v.listingCount ?? 0})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {effectiveVenue ? (
        <VenueGroups venue={effectiveVenue} />
      ) : (
        <p className="text-sm text-muted-foreground">No venues with inventory yet.</p>
      )}
    </div>
  );
}

function VenueGroups({ venue }: { venue: string }) {
  const qc = useQueryClient();
  const { data: groups, isLoading } = useListPricingGroups({ venue });
  const [editing, setEditing] = useState<PricingGroup | null>(null);
  const [creating, setCreating] = useState(false);

  const del = useDeletePricingGroup({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getListPricingGroupsQueryKey({ venue }) });
        toast.success("Group deleted");
      },
      onError: () => toast.error("Failed to delete group"),
    },
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Groups</span>
        <Button size="sm" className="h-8" onClick={() => setCreating(true)}>
          <Plus className="h-3.5 w-3.5 mr-1" /> New Group
        </Button>
      </div>
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
        </div>
      ) : (groups ?? []).length === 0 ? (
        <p className="text-sm text-muted-foreground">No groups for this venue yet.</p>
      ) : (
        <div className="space-y-2">
          {(groups ?? []).map((g) => (
            <div
              key={g.id}
              className="border border-border rounded p-3 flex items-start justify-between gap-3"
            >
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm">{g.name}</span>
                  {g.isGaGroup && <Badge variant="secondary">GA</Badge>}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {g.members.map((m, i) => {
                    const rows = formatRange(m);
                    return rows ? (
                      <span
                        key={m.id ?? i}
                        className="inline-flex items-center overflow-hidden rounded border border-primary/40 font-mono text-[10px]"
                      >
                        <span className="px-1.5 py-px">{m.section}</span>
                        <span className="border-l border-primary/40 bg-primary/10 px-1.5 py-px font-medium text-primary">
                          {rows}
                        </span>
                      </span>
                    ) : (
                      <Badge key={m.id ?? i} variant="outline" className="font-mono text-[10px]">
                        {m.section}
                      </Badge>
                    );
                  })}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  onClick={() => setEditing(g)}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7 text-destructive"
                  onClick={() => {
                    if (confirm(`Delete group "${g.name}"?`)) del.mutate({ id: g.id });
                  }}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
      {(creating || editing) && (
        <GroupEditorDialog
          venue={venue}
          group={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function GroupEditorDialog({
  venue,
  group,
  onClose,
}: {
  venue: string;
  group: PricingGroup | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { data: sections } = useListPricingSections({ venue });
  const { data: allGroups } = useListPricingGroups({ venue });
  const [name, setName] = useState(group?.name ?? "");
  const [members, setMembers] = useState<PricingGroupMemberInput[]>(
    group?.members.map((m) => ({
      section: m.section,
      rowFrom: m.rowFrom ?? null,
      rowTo: m.rowTo ?? null,
    })) ?? [],
  );

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: getListPricingGroupsQueryKey({ venue }) });

  const create = useCreatePricingGroup({
    mutation: {
      onSuccess: () => {
        invalidate();
        toast.success("Group created");
        onClose();
      },
      onError: () => toast.error("Failed to create group"),
    },
  });
  const update = useUpdatePricingGroup({
    mutation: {
      onSuccess: () => {
        invalidate();
        toast.success("Group updated");
        onClose();
      },
      onError: () => toast.error("Failed to update group"),
    },
  });

  // A section is only fully locked when another group owns it with no row range.
  // If another group claims only a row range, the section stays selectable here
  // so the remaining rows can be assigned to this group; we surface the taken
  // ranges as a read-only hint in the row popover.
  const lockedSections = new Set<string>();
  const claimedRanges = new Map<
    string,
    { group: string; label: string; from: string | null; to: string | null }[]
  >();
  for (const g of allGroups ?? []) {
    if (group && g.id === group.id) continue;
    for (const m of g.members) {
      const norm = normSection(m.section);
      if (!m.rowFrom && !m.rowTo) {
        lockedSections.add(norm);
        continue;
      }
      const label = formatRange(m);
      if (!label) continue;
      const arr = claimedRanges.get(norm) ?? [];
      arr.push({
        group: g.name,
        label,
        from: m.rowFrom ?? null,
        to: m.rowTo ?? null,
      });
      claimedRanges.set(norm, arr);
    }
  }

  // Advisory overlap check: a member's row range overlapping a range another
  // group already claims for the same section creates ambiguous (first-match-
  // wins) bulk-push matching. We surface it as a non-blocking warning — whole-
  // section conflicts are still hard-blocked via lockedSections above.
  const conflicts = members.flatMap((m) => {
    const norm = normSection(m.section);
    const claimed = claimedRanges.get(norm) ?? [];
    return claimed
      .filter((c) => rangesOverlap(m.rowFrom, m.rowTo, c.from, c.to))
      .map((c) => ({
        section: m.section,
        rows: formatRange(m) || "all rows",
        group: c.group,
        otherRows: c.label,
      }));
  });

  // Show every venue section as a chip, plus any already-selected section the
  // venue list doesn't return (e.g. a section with no current listings) so it
  // stays visible and can be deselected.
  const sectionList: PricingSection[] = [...(sections ?? [])];
  const known = new Set(sectionList.map((s) => normSection(s.section)));
  for (const m of members) {
    const norm = normSection(m.section);
    if (!known.has(norm)) {
      sectionList.push({ section: m.section, isGa: isGa(m.section), listingCount: 0 });
      known.add(norm);
    }
  }

  const toggleSection = (section: string) => {
    setMembers((prev) => {
      const norm = normSection(section);
      return prev.some((m) => normSection(m.section) === norm)
        ? prev.filter((m) => normSection(m.section) !== norm)
        : [...prev, { section, rowFrom: null, rowTo: null }];
    });
  };

  const setRange = (
    section: string,
    patch: { rowFrom?: string | null; rowTo?: string | null },
  ) => {
    setMembers((prev) =>
      prev.map((m) =>
        normSection(m.section) === normSection(section) ? { ...m, ...patch } : m,
      ),
    );
  };

  const submit = () => {
    if (!name.trim()) {
      toast.error("Group name is required");
      return;
    }
    if (members.length === 0) {
      toast.error("Add at least one section");
      return;
    }
    const cleaned = members.map((m) => ({
      section: m.section,
      rowFrom: m.rowFrom?.trim() ? m.rowFrom.trim() : null,
      rowTo: m.rowTo?.trim() ? m.rowTo.trim() : null,
    }));
    if (group) {
      update.mutate({ id: group.id, data: { name: name.trim(), members: cleaned } });
    } else {
      create.mutate({ data: { venue, name: name.trim(), members: cleaned } });
    }
  };

  const busy = create.isPending || update.isPending;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{group ? "Edit Group" : "New Group"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Group name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Lower Bowl"
              className="h-8 text-sm"
            />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Sections ({venue})</Label>
              <span className="text-[11px] text-muted-foreground">
                {members.length} selected
              </span>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Click a section to add it. Use the{" "}
              <SlidersHorizontal className="inline h-3 w-3 -mt-0.5" /> icon on a
              selected section to limit it to a row range.
            </p>
            {sectionList.length > 0 ? (
              <SectionPickerPanel
                sections={sectionList}
                members={members}
                lockedSections={lockedSections}
                claimedRanges={claimedRanges}
                onToggle={toggleSection}
                onSetRange={setRange}
              />
            ) : (
              <p className="text-xs text-muted-foreground">
                No sections found for this venue.
              </p>
            )}
          </div>
          {conflicts.length > 0 && (
            <div className="rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-700 dark:text-amber-400 space-y-1">
              <div className="flex items-center gap-1.5 font-medium">
                <AlertTriangle className="h-3.5 w-3.5" />
                Overlapping rows
              </div>
              {conflicts.map((c, i) => (
                <div key={i}>
                  <span className="font-mono">{c.section}</span> rows {c.rows}{" "}
                  overlap rows {c.otherRows} in{" "}
                  <span className="font-medium">{c.group}</span>
                </div>
              ))}
              <div className="text-amber-600/80 dark:text-amber-500/80">
                Bulk-push matching is first-match-wins for overlapping rows. You
                can still save.
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy}>
            {busy && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
            {group ? "Save" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


type SectionBucket = { label: string; sections: PricingSection[] };

function compareSections(a: PricingSection, b: PricingSection): number {
  const an = a.section.trim();
  const bn = b.section.trim();
  const aNum = /^\d+$/.test(an);
  const bNum = /^\d+$/.test(bn);
  if (aNum && bNum) return Number(an) - Number(bn);
  return an.localeCompare(bn, undefined, { numeric: true });
}

// Classify sections into named, ordered buckets for the chip picker. Pure-numeric
// sections group by floor-to-100 ("100s", "200s"…), GA variants into "GA", alpha
// sections by first letter (collapsed into ranges like "A–D" when there are many),
// and anything else into "Other".
function bucketSections(sections: PricingSection[]): SectionBucket[] {
  const numeric = new Map<number, PricingSection[]>();
  const alpha = new Map<string, PricingSection[]>();
  const ga: PricingSection[] = [];
  const other: PricingSection[] = [];

  for (const s of sections) {
    const name = (s.section ?? "").trim();
    if (s.isGa || isGa(name)) {
      ga.push(s);
      continue;
    }
    if (/^\d+$/.test(name)) {
      const base = Math.floor(Number(name) / 100) * 100;
      const arr = numeric.get(base) ?? [];
      arr.push(s);
      numeric.set(base, arr);
      continue;
    }
    const first = name.charAt(0).toUpperCase();
    if (first >= "A" && first <= "Z") {
      const arr = alpha.get(first) ?? [];
      arr.push(s);
      alpha.set(first, arr);
      continue;
    }
    other.push(s);
  }

  const buckets: SectionBucket[] = [];

  for (const base of [...numeric.keys()].sort((a, b) => a - b)) {
    const arr = numeric.get(base)!.sort(compareSections);
    buckets.push({ label: `${base}s`, sections: arr });
  }

  const letters = [...alpha.keys()].sort();
  if (letters.length > 6) {
    for (let i = 0; i < letters.length; i += 4) {
      const chunk = letters.slice(i, i + 4);
      const label =
        chunk.length > 1 ? `${chunk[0]}–${chunk[chunk.length - 1]}` : chunk[0];
      const arr = chunk.flatMap((l) => alpha.get(l)!).sort(compareSections);
      buckets.push({ label, sections: arr });
    }
  } else {
    for (const l of letters) {
      buckets.push({ label: l, sections: alpha.get(l)!.sort(compareSections) });
    }
  }

  if (ga.length) buckets.push({ label: "GA", sections: ga.sort(compareSections) });
  if (other.length)
    buckets.push({ label: "Other", sections: other.sort(compareSections) });

  return buckets;
}

function formatRange(m: PricingGroupMemberInput): string {
  const from = m.rowFrom?.trim();
  const to = m.rowTo?.trim();
  if (from && to) return from === to ? from : `${from}–${to}`;
  if (from) return `${from}+`;
  if (to) return `≤${to}`;
  return "";
}

function SectionPickerPanel({
  sections,
  members,
  lockedSections,
  claimedRanges,
  onToggle,
  onSetRange,
}: {
  sections: PricingSection[];
  members: PricingGroupMemberInput[];
  lockedSections: Set<string>;
  claimedRanges: Map<string, { group: string; label: string }[]>;
  onToggle: (section: string) => void;
  onSetRange: (
    section: string,
    patch: { rowFrom?: string | null; rowTo?: string | null },
  ) => void;
}) {
  const buckets = bucketSections(sections);
  const memberMap = new Map(members.map((m) => [normSection(m.section), m]));
  if (buckets.length === 0) return null;
  return (
    <TooltipProvider delayDuration={200}>
      <div className="max-h-60 overflow-y-auto rounded border border-border p-2 space-y-2">
        {buckets.map((bucket) => (
          <div key={bucket.label} className="space-y-1">
            <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              {bucket.label}
            </div>
            <div className="flex flex-wrap gap-1">
              {bucket.sections.map((s) => {
                const norm = normSection(s.section);
                const member = memberMap.get(norm);
                const selected = !!member;
                // A section is only locked when another group owns the WHOLE
                // section (no row range) — unless it is already a member of THIS
                // group, which must stay deselectable.
                const locked = !selected && lockedSections.has(norm);
                if (locked) {
                  return (
                    <Tooltip key={s.section}>
                      <TooltipTrigger asChild>
                        <span className="inline-flex items-center rounded border border-border bg-muted px-2 py-0.5 font-mono text-xs opacity-40 cursor-not-allowed">
                          {s.section}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>Already in another group</TooltipContent>
                    </Tooltip>
                  );
                }
                const sectionIsGa = s.isGa || isGa(s.section);
                const range = member ? formatRange(member) : "";
                const claimed = claimedRanges.get(norm) ?? [];
                return (
                  <span
                    key={s.section}
                    className={cn(
                      "inline-flex items-center rounded border font-mono text-xs transition-colors",
                      selected
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border hover:border-primary hover:bg-accent",
                    )}
                  >
                    <button
                      type="button"
                      aria-pressed={selected}
                      onClick={() => onToggle(s.section)}
                      className="inline-flex items-center px-2 py-0.5"
                    >
                      {s.section}
                      {selected && range ? (
                        <span className="ml-1 text-[10px] opacity-80">
                          rows {range}
                        </span>
                      ) : (
                        <span
                          className={cn(
                            "ml-1 text-[10px]",
                            selected ? "opacity-70" : "text-muted-foreground",
                          )}
                        >
                          {s.listingCount ?? 0}
                        </span>
                      )}
                    </button>
                    {selected && !sectionIsGa && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            aria-label={`Row filter for ${s.section}`}
                            className={cn(
                              "flex items-center self-stretch border-l border-primary-foreground/30 px-1 hover:bg-primary-foreground/10",
                              range ? "opacity-100" : "opacity-70",
                            )}
                          >
                            <SlidersHorizontal className="h-3 w-3" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent align="start" className="w-60 space-y-2">
                          <div className="text-xs font-medium font-mono">
                            {s.section}
                          </div>
                          <p className="text-[11px] text-muted-foreground">
                            Limit this section to a row range. Leave blank to
                            include all rows.
                          </p>
                          {claimed.length > 0 && (
                            <div className="rounded border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-[10px] text-amber-700 dark:text-amber-400 space-y-0.5">
                              {claimed.map((c, i) => (
                                <div key={i}>
                                  Rows {c.label} already in{" "}
                                  <span className="font-medium">{c.group}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          <div className="flex items-center gap-2">
                            <Input
                              value={member?.rowFrom ?? ""}
                              onChange={(e) =>
                                onSetRange(s.section, { rowFrom: e.target.value })
                              }
                              placeholder="From"
                              className="h-7 text-xs"
                            />
                            <span className="text-muted-foreground text-xs">to</span>
                            <Input
                              value={member?.rowTo ?? ""}
                              onChange={(e) =>
                                onSetRange(s.section, { rowTo: e.target.value })
                              }
                              placeholder="To"
                              className="h-7 text-xs"
                            />
                          </div>
                          {range && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-full text-xs"
                              onClick={() =>
                                onSetRange(s.section, { rowFrom: null, rowTo: null })
                              }
                            >
                              Clear row filter
                            </Button>
                          )}
                        </PopoverContent>
                      </Popover>
                    )}
                  </span>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </TooltipProvider>
  );
}

const GA_LABEL = "General Admission";

function isGa(section: string | null | undefined): boolean {
  if (!section) return false;
  const s = section.trim().toLowerCase();
  return s === "ga" || s === GA_LABEL.toLowerCase() || s.includes("general admission");
}

function normSection(section: string | null | undefined): string {
  if (!section) return "";
  if (isGa(section)) return GA_LABEL.toLowerCase();
  return section.trim().toLowerCase();
}

function rowKey(row: string): { kind: "num" | "str"; value: number | string } {
  const t = (row ?? "").trim();
  if (/^\d+$/.test(t)) return { kind: "num", value: Number(t) };
  return { kind: "str", value: t.toUpperCase() };
}

function rowInRange(
  row: string | null | undefined,
  from: string | null | undefined,
  to: string | null | undefined,
): boolean {
  if (!from && !to) return true;
  if (!row) return false;
  const r = rowKey(row);
  if (from) {
    const f = rowKey(from);
    if (f.kind !== r.kind) return false;
    if (r.value < f.value) return false;
  }
  if (to) {
    const tt = rowKey(to);
    if (tt.kind !== r.kind) return false;
    if (r.value > tt.value) return false;
  }
  return true;
}

// Resolve a row range into a comparable kind + lo/hi. Open ends stay null.
function rangeBounds(from: string | null | undefined, to: string | null | undefined): {
  kind: "num" | "str" | null;
  lo: number | string | null;
  hi: number | string | null;
} {
  const fk = from?.trim() ? rowKey(from) : null;
  const tk = to?.trim() ? rowKey(to) : null;
  return { kind: fk?.kind ?? tk?.kind ?? null, lo: fk?.value ?? null, hi: tk?.value ?? null };
}

// True when two row ranges overlap. Ranges of different kinds (numeric vs
// alpha rows) can never overlap; open ends behave as -inf / +inf.
function rangesOverlap(
  aFrom: string | null | undefined,
  aTo: string | null | undefined,
  bFrom: string | null | undefined,
  bTo: string | null | undefined,
): boolean {
  const a = rangeBounds(aFrom, aTo);
  const b = rangeBounds(bFrom, bTo);
  if (a.kind && b.kind && a.kind !== b.kind) return false;
  if (a.hi != null && b.lo != null && b.lo > a.hi) return false;
  if (b.hi != null && a.lo != null && a.lo > b.hi) return false;
  return true;
}

function listingMatchesMember(l: InventoryListing, m: PricingGroupMember): boolean {
  const memberIsGa = m.section === GA_LABEL || isGa(m.section);
  if (memberIsGa) {
    if (!isGa(l.section)) return false;
  } else if (normSection(l.section) !== normSection(m.section)) {
    return false;
  }
  return rowInRange(l.row ?? null, m.rowFrom ?? null, m.rowTo ?? null);
}

// Mirror of the backend computePrices ordering (lib/pricing.ts) so the pre-push
// preview shows exactly what the server will assign per listing. Pure-numeric
// rows order numerically, alpha rows base-26, everything else by string.
function waterfallRowKey(
  row: string,
): { kind: "num" | "alpha" | "str"; value: number | string } {
  const trimmed = (row ?? "").trim();
  if (/^\d+$/.test(trimmed)) return { kind: "num", value: Number(trimmed) };
  if (/^[A-Za-z]+$/.test(trimmed)) {
    const upper = trimmed.toUpperCase();
    let v = 0;
    for (const ch of upper) v = v * 26 + (ch.charCodeAt(0) - 64);
    return { kind: "alpha", value: v };
  }
  return { kind: "str", value: trimmed.toUpperCase() };
}

function compareListingsForWaterfall(a: InventoryListing, b: InventoryListing): number {
  const sa = (a.section ?? "").toUpperCase();
  const sb = (b.section ?? "").toUpperCase();
  if (sa !== sb) return sa < sb ? -1 : 1;
  const ra = waterfallRowKey(a.row ?? "");
  const rb = waterfallRowKey(b.row ?? "");
  if (ra.kind === rb.kind && ra.value !== rb.value) return ra.value < rb.value ? -1 : 1;
  if (ra.kind !== rb.kind) {
    const x = String(ra.value);
    const y = String(rb.value);
    if (x !== y) return x < y ? -1 : 1;
  }
  const fa = a.seatFrom ?? "";
  const fb = b.seatFrom ?? "";
  if (/^\d+$/.test(fa) && /^\d+$/.test(fb)) return Number(fa) - Number(fb);
  if (fa !== fb) return fa < fb ? -1 : 1;
  return a.id - b.id;
}

interface PreviewEntry {
  listing: InventoryListing;
  price: number;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function computePreviewPrices(
  listings: InventoryListing[],
  opts: {
    strategy: "flat" | "waterfall";
    basePrice: number;
    increment?: number | null;
    direction?: "up" | "down" | null;
  },
): PreviewEntry[] {
  if (opts.strategy === "flat") {
    const price = Math.max(0, round2(opts.basePrice));
    return listings.map((l) => ({ listing: l, price }));
  }
  const increment = opts.increment ?? 0;
  const sign = opts.direction === "down" ? -1 : 1;
  const ordered = [...listings].sort(compareListingsForWaterfall);
  return ordered.map((l, idx) => ({
    listing: l,
    price: Math.max(0, round2(opts.basePrice + sign * increment * idx)),
  }));
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: "$",
  GBP: "£",
  EUR: "€",
  CAD: "$",
  AUD: "$",
};

function currencySymbol(code: string | null | undefined): string {
  if (!code) return "$";
  return CURRENCY_SYMBOLS[code.toUpperCase()] ?? "$";
}

function fmtMoney(sym: string, n: number): string {
  return `${sym}${n.toFixed(2)}`;
}

function recRange(listings: InventoryListing[], sym: string): string {
  const vals = listings
    .map((l) => l.recommendedListPrice)
    .filter((v): v is number => v != null);
  if (vals.length === 0) return "—";
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  return min === max ? fmtMoney(sym, min) : `${fmtMoney(sym, min)}–${fmtMoney(sym, max)}`;
}

interface Bucket {
  key: string;
  label: string;
  groupId: number | null;
  isGaGroup: boolean;
  listings: InventoryListing[];
}

function BulkUpdateTab() {
  const { data: events } = useListEvents();
  const [eventId, setEventId] = useState<string>("");
  const [venue, setVenue] = useState<string>("");

  const enabledEvents = (events ?? []).filter((e: Event) => e.enabled);
  const numericEventId = Number(eventId) || 0;

  const { data: inventory, isLoading: invLoading } = useListEventInventory(numericEventId, {
    query: {
      enabled: numericEventId > 0,
      queryKey: getListEventInventoryQueryKey(numericEventId),
    },
  });

  const remaining = (inventory ?? []).filter((l) => (l.availableQuantity ?? 0) > 0);
  const venuesInEvent = Array.from(
    new Set(remaining.map((l) => l.venue).filter((v): v is string => !!v)),
  ).sort();
  const effectiveVenue = venue || venuesInEvent[0] || "";

  const { data: groups } = useListPricingGroups(
    effectiveVenue ? { venue: effectiveVenue } : undefined,
  );

  const venueListings = remaining.filter((l) => l.venue === effectiveVenue);

  const buckets: Bucket[] = [];
  const assigned = new Set<number>();
  for (const g of groups ?? []) {
    const matched = venueListings.filter(
      (l) => !assigned.has(l.id) && g.members.some((m) => listingMatchesMember(l, m)),
    );
    matched.forEach((l) => assigned.add(l.id));
    buckets.push({
      key: `g${g.id}`,
      label: g.name,
      groupId: g.id,
      isGaGroup: g.isGaGroup,
      listings: matched,
    });
  }
  const ungrouped = venueListings.filter((l) => !assigned.has(l.id));
  buckets.push({
    key: "ungrouped",
    label: "Ungrouped",
    groupId: null,
    isGaGroup: false,
    listings: ungrouped,
  });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
        <div className="space-y-1.5">
          <Label className="text-xs">Event</Label>
          <Select
            value={eventId}
            onValueChange={(v) => {
              setEventId(v);
              setVenue("");
            }}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="Select event" />
            </SelectTrigger>
            <SelectContent>
              {enabledEvents.map((e) => (
                <SelectItem key={e.id} value={String(e.id)}>
                  {e.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {venuesInEvent.length > 1 && (
          <div className="space-y-1.5">
            <Label className="text-xs">Venue</Label>
            <Select value={effectiveVenue} onValueChange={setVenue}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {venuesInEvent.map((v) => (
                  <SelectItem key={v} value={v}>
                    {v}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {!eventId ? (
        <p className="text-sm text-muted-foreground">
          Select an event to see its remaining inventory grouped by pricing group.
        </p>
      ) : invLoading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
        </div>
      ) : venueListings.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No remaining inventory for this event{effectiveVenue ? ` at ${effectiveVenue}` : ""}.
        </p>
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">
            {venueListings.length} remaining listing
            {venueListings.length === 1 ? "" : "s"}
            {effectiveVenue ? ` at ${effectiveVenue}` : ""} ·{" "}
            {(groups ?? []).length} group{(groups ?? []).length === 1 ? "" : "s"}
          </p>
          {buckets.map((b) => (
            <GroupPushPanel
              key={b.key}
              eventId={numericEventId}
              bucket={b}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function GroupPushPanel({ eventId, bucket }: { eventId: number; bucket: Bucket }) {
  const qc = useQueryClient();
  const [strategy, setStrategy] = useState<"flat" | "waterfall">("flat");
  const [basePrice, setBasePrice] = useState<string>("");
  const [increment, setIncrement] = useState<string>("");
  const [direction, setDirection] = useState<"up" | "down">("down");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [lastResult, setLastResult] = useState<BulkPricePushResult | null>(null);

  const sym = currencySymbol(bucket.listings[0]?.currencyCode);
  const count = bucket.listings.length;

  const push = useBulkPushPricing({
    mutation: {
      onSuccess: (data) => {
        setLastResult(data);
        setConfirmOpen(false);
        qc.invalidateQueries({ queryKey: getListEventInventoryQueryKey(eventId) });
        toast.success(
          `Updated ${data.updatedCount} listing${data.updatedCount === 1 ? "" : "s"}` +
            (data.slackFailed ? ` · ${data.slackFailed} Slack alerts failed` : ""),
        );
      },
      onError: () => toast.error("Bulk push failed"),
    },
  });

  const base = Number(basePrice);
  const inc = Number(increment);
  const baseValid = !!basePrice && !Number.isNaN(base) && base > 0;
  const incValid = strategy === "flat" || (!!increment && !Number.isNaN(inc) && inc > 0);
  const inputsValid = baseValid && incValid;

  // Live, authoritative per-listing preview (mirrors the server's computePrices)
  // recomputed on every input change so the pricer sees exactly what a push will
  // do before committing.
  const previewEntries =
    inputsValid && count > 0
      ? computePreviewPrices(bucket.listings, {
          strategy,
          basePrice: base,
          increment: strategy === "waterfall" ? inc : null,
          direction: strategy === "waterfall" ? direction : null,
        })
      : [];

  const previewRange = (() => {
    if (previewEntries.length === 0) return null;
    const prices = previewEntries.map((e) => e.price);
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    return min === max ? fmtMoney(sym, min) : `${fmtMoney(sym, min)}–${fmtMoney(sym, max)}`;
  })();

  const openConfirm = () => {
    if (count === 0) {
      toast.error("No listings in this group");
      return;
    }
    if (!baseValid) {
      toast.error("Enter a valid price");
      return;
    }
    if (!incValid) {
      toast.error("Enter a valid waterfall increment");
      return;
    }
    setConfirmOpen(true);
  };

  const confirmPush = () => {
    push.mutate({
      data: {
        eventId,
        groupId: bucket.groupId,
        listingIds: bucket.listings.map((l) => l.id),
        strategy,
        basePrice: base,
        increment: strategy === "waterfall" ? inc : null,
        direction: strategy === "waterfall" ? direction : undefined,
      },
    });
  };

  return (
    <div className="border border-border rounded p-3 space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm">{bucket.label}</span>
          {bucket.isGaGroup && <Badge variant="secondary">GA</Badge>}
          <Badge variant="outline" className="font-mono text-[10px]">
            {count} listing{count === 1 ? "" : "s"}
          </Badge>
        </div>
        <span className="text-xs text-muted-foreground">
          Current rec: {recRange(bucket.listings, sym)}
        </span>
      </div>

      {count === 0 ? (
        <p className="text-xs rounded border border-dashed border-border bg-muted/40 px-2 py-1.5 text-muted-foreground">
          No remaining listings match this group for the selected event — nothing to push.
        </p>
      ) : (
        <div className="flex items-end gap-2 flex-wrap">
          <div className="space-y-1">
            <Label className="text-[10px] text-muted-foreground">Strategy</Label>
            <Select value={strategy} onValueChange={(v) => setStrategy(v as "flat" | "waterfall")}>
              <SelectTrigger className="h-8 w-40 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="flat">Flat (same price)</SelectItem>
                <SelectItem value="waterfall">Waterfall (stepped)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] text-muted-foreground">
              {strategy === "flat" ? "Price" : "Base price"}
            </Label>
            <Input
              type="number"
              value={basePrice}
              onChange={(e) => setBasePrice(e.target.value)}
              placeholder="0.00"
              className="h-8 w-28 text-sm"
            />
          </div>
          {strategy === "waterfall" && (
            <>
              <div className="space-y-1">
                <Label className="text-[10px] text-muted-foreground">Increment</Label>
                <Input
                  type="number"
                  value={increment}
                  onChange={(e) => setIncrement(e.target.value)}
                  placeholder="5"
                  className="h-8 w-24 text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] text-muted-foreground">Direction</Label>
                <Select value={direction} onValueChange={(v) => setDirection(v as "up" | "down")}>
                  <SelectTrigger className="h-8 w-44 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="down">Decrease down rows</SelectItem>
                    <SelectItem value="up">Increase down rows</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
          <Button
            className="h-8"
            onClick={openConfirm}
            disabled={push.isPending || !inputsValid}
          >
            <Send className="h-3.5 w-3.5 mr-1.5" /> Push
          </Button>
          {lastResult && (
            <span className="text-[11px] text-muted-foreground">
              Updated {lastResult.updatedCount}
              {lastResult.slackSent != null ? ` · ${lastResult.slackSent} alerts` : ""}
            </span>
          )}
        </div>
      )}

      {count > 0 && (
        <p className="text-xs">
          {inputsValid ? (
            <span className="text-muted-foreground">
              Will update{" "}
              <span className="font-medium text-foreground">
                {count} listing{count === 1 ? "" : "s"}
              </span>{" "}
              · new recommended price{strategy === "flat" ? "" : " range"}:{" "}
              <span className="font-mono text-foreground">{previewRange}</span>
            </span>
          ) : (
            <span className="text-muted-foreground">
              Enter a {strategy === "flat" ? "price" : "base price and increment"} to preview
              the update.
            </span>
          )}
        </p>
      )}

      <Dialog open={confirmOpen} onOpenChange={(o) => !o && setConfirmOpen(false)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Confirm bulk price push</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <p>
              Push a <strong>{strategy === "flat" ? "flat" : "waterfall"}</strong> recommended
              price to <strong>{count}</strong> listing{count === 1 ? "" : "s"} in{" "}
              <strong>{bucket.label}</strong>.
            </p>
            <p className="text-muted-foreground">
              Resulting recommended price{strategy === "flat" ? "" : " range"}:{" "}
              <span className="font-mono text-foreground">{previewRange ?? "—"}</span>
            </p>
            {previewEntries.length > 0 && (
              <div className="rounded border border-border">
                <div className="grid grid-cols-[1fr_auto] gap-x-3 border-b border-border bg-muted/40 px-2 py-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  <span>Listing</span>
                  <span className="text-right">New price</span>
                </div>
                <div className="max-h-48 overflow-auto divide-y divide-border">
                  {previewEntries.map((e) => (
                    <div
                      key={e.listing.id}
                      className="grid grid-cols-[1fr_auto] gap-x-3 px-2 py-1 text-xs"
                    >
                      <span className="truncate font-mono">
                        {e.listing.section || "—"}
                        {e.listing.row ? ` · Row ${e.listing.row}` : ""}
                        {e.listing.seatFrom
                          ? ` · ${e.listing.seatFrom}${
                              e.listing.seatTo && e.listing.seatTo !== e.listing.seatFrom
                                ? `–${e.listing.seatTo}`
                                : ""
                            }`
                          : ""}
                      </span>
                      <span className="text-right font-mono">{fmtMoney(sym, e.price)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Affected pullers will get an in-app pending flag and a Slack alert.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)} disabled={push.isPending}>
              Cancel
            </Button>
            <Button onClick={confirmPush} disabled={push.isPending}>
              {push.isPending && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
              Push to {count} listing{count === 1 ? "" : "s"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
